import { Component, inject, signal } from '@angular/core';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { FormsModule, NgForm } from '@angular/forms';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { AcademicYearService } from '../../../../../services/academic-year';
import { IAcademicYear, IAcademicYears } from '../../../../../interfaces/academic-year';
import { FlashService } from '../../../../../services/flash';
import { Helpers } from '../../../../../utilities/helpers';
import { Session } from '../../../../../services/session';
import { SchoolService } from '../../../../../services/school';
import { ISchools } from '../../../../../interfaces/school';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-academic-years',
  imports: [ActionButton, FormsModule, LoadingIndicator, NotFoundError, Pagination, NgClass],
  templateUrl: './academic-years.html',
  styleUrl: './academic-years.css',
})
export class AcademicYears {
  constructor(
    protected academicYearService: AcademicYearService,
    private session: Session,
    private schoolService: SchoolService,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole === 'SUPER_ADMIN') {
      this.getSchools();
    }

    this.getAcademicYears();
  }

  protected academicYears = signal<IAcademicYears | null>(null);
  protected readonly flashService = inject(FlashService);
  protected loggedUserRole?: string;
  protected schoolId?: string;
  protected pagination = signal({ page: 0, size: 10 });
  protected readonly Helpers = Helpers;
  protected schools = signal<ISchools | null>(null);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected search: string = '';
  protected filterSchoolId: string = '';
  protected status: string = '';
  protected filterStatus: string = '';
  protected isFiltering = signal<boolean>(false);
  protected selectedAcademicYear!: IAcademicYear['payload'];
  protected academicYearName = '';
  protected startDate = '';
  protected endDate = '';
  protected settingCurrentYearId = signal<string>('');
  protected changeStatus = signal<boolean>(false);
  protected editAcademicYear = signal<boolean>(false);
  protected isEditing = signal<boolean>(false);
  protected validStatuses = ['ACTIVE', 'UPCOMING', 'COMPLETED', 'INACTIVE', 'ARCHIVED'];

  protected getAcademicYears(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
  ): void {
    this.isFiltering.set(true);

    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }
    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Could not load Academic Years. Refresh the page.');
        },
      });
  }

  protected getSchools(): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getAcademicYears();
  }

  protected selectAcademicYear(action: string, payload: IAcademicYear['payload']): void {
    if (action === 'status') {
      this.changeStatus.set(true);
    } else {
      this.editAcademicYear.set(true);
    }

    this.selectedAcademicYear = payload;
    this.academicYearName = this.selectedAcademicYear.name;
    this.startDate = new Date(this.selectedAcademicYear.start_date).toISOString().split('T')[0];
    this.endDate = new Date(this.selectedAcademicYear.end_date).toISOString().split('T')[0];
  }

  protected setCurrentAcademicYear(payload: IAcademicYear['payload']): void {
    this.selectedAcademicYear = payload;
    this.settingCurrentYearId.set(payload.id);

    this.academicYearService
      .editAcademicYear(`${ApiEndpoints.url}/academic-year/${payload.id}`, {
        current_academic_year: true,
      })
      .subscribe({
        next: () => {
          this.settingCurrentYearId.set('');
          this.flashService.success('Academic year set as current successfully');
          this.getAcademicYears();
        },
        error: (err) => {
          this.settingCurrentYearId.set('');
          this.flashService.failure(err?.error?.message ?? 'Unable to set current academic year');
        },
      });
  }

  protected editAcademicYearFn(form: NgForm, action: 'status' | 'edit'): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    const payload: Record<string, any> = {};

    if (action === 'status') {
      if (this.status !== this.selectedAcademicYear.status) {
        payload['status'] = this.status;
      }
    } else {
      if (this.startDate !== this.selectedAcademicYear.start_date) {
        payload['start_date'] = this.startDate;
      }

      if (this.endDate !== this.selectedAcademicYear.end_date) {
        payload['end_date'] = this.endDate;
      }
    }

    if (!Object.keys(payload).length) {
      this.flashService.failure('No changes detected');
      return;
    }

    this.isEditing.set(true);

    this.academicYearService
      .editAcademicYear(
        `${ApiEndpoints.url}/academic-year/${this.selectedAcademicYear.id}`,
        payload,
      )
      .subscribe({
        next: () => {
          this.isEditing.set(false);

          this.changeStatus.set(false);
          this.editAcademicYear.set(false);

          this.flashService.success(
            action === 'status'
              ? 'Academic year status updated successfully'
              : 'Academic year updated successfully',
          );

          this.getAcademicYears();
          this.status = '';
        },

        error: (err) => {
          this.isEditing.set(false);

          this.flashService.failure(err?.error?.message ?? 'Unable to update academic year');
        },
      });
  }
}
